import { normalizeSrCharts, type SrChatChart } from "./sr-chat-chart-types";
import {
    normalizeNameCandidates,
    type SrNameCandidate,
} from "./sr-name-candidate-types";
import { type SrChatMessage, welcome } from "./sr-chat-types";

export type SrSavedChat = {
    conversation_id: string;
    title: string;
    timestamp: number;
};

export type SrSavedTurn = {
    timestamp: number;
    user_query: string;
    reply: string;
    charts: SrChatChart[];
    name_candidates: SrNameCandidate[];
};

type ApiPost = (
    url: string,
    payload: object,
    errorMessage: string
) => Promise<{ data?: { chats?: unknown; turns?: unknown } }>;

export const normalizeChats = (raw: unknown): SrSavedChat[] => {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((row) => {
            const r = row as SrSavedChat;
            const id = String(r.conversation_id || "");
            if (!id) return null;
            return {
                conversation_id: id,
                title: String(r.title || "Chat").trim() || "Chat",
                timestamp: Number(r.timestamp || 0),
            };
        })
        .filter((x): x is SrSavedChat => x != null);
};

export const turnsToMessages = (turns: SrSavedTurn[]): SrChatMessage[] => {
    const msgs: SrChatMessage[] = [welcome];
    turns.forEach((t, i) => {
        const ts = t.timestamp || i;
        if (t.user_query) {
            msgs.push({
                id: `u-${ts}-${i}`,
                role: "user",
                content: t.user_query,
            });
        }
        const charts = normalizeSrCharts(t.charts);
        const names = normalizeNameCandidates(t.name_candidates);
        if (t.reply || charts.length || names.length) {
            msgs.push({
                id: `a-${ts}-${i}`,
                role: "assistant",
                content: t.reply || "",
                charts: charts.length ? charts : undefined,
                nameCandidates: names.length ? names : undefined,
            });
        }
    });
    return msgs;
};

export const fetchSrChats = async (api: ApiPost, projectid: number) => {
    const response = await api(
        "unified_service_request/sr-agent/chats",
        { projectid },
        "Chat list failed"
    );
    return normalizeChats(response?.data?.chats);
};

export const fetchSrChat = async (
    api: ApiPost,
    projectid: number,
    conversationId: string
) => {
    const response = await api(
        "unified_service_request/sr-agent/open-chat",
        { projectid, conversation_id: conversationId },
        "Chat load failed"
    );
    const turns = Array.isArray(response?.data?.turns)
        ? (response.data.turns as SrSavedTurn[])
        : [];
    return turnsToMessages(turns);
};
